import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { projects } from "../../../content";
import type { Project } from "../../../types/content";
import { CATEGORY_ACCENT, CATEGORY_LABEL } from "./Projects";

/**
 * ============================================================================
 * Projects — the Home teaser.
 * ============================================================================
 *
 * Four flagship client builds and a link to /projects.
 *
 * Same rule as the Products teaser: WHICH four is editorial and lives here, and
 * the FACTS are resolved from the layer by id so a corrected name or role
 * reaches this strip without anyone remembering the strip exists (D-065).
 *
 * Owner pick, 2026-08-20 — the revenue/scale set. It leads with the US
 * healthcare platform because the active search targets US healthcare insurance
 * technology and that row carries the strongest evidence in the file.
 */

/* THE CATEGORY LABEL AND HUE COME FROM THE PAGE, NOT FROM A SECOND COPY HERE.
   `/projects` owns both maps and the teaser now shows the same pill, so a
   category renamed or recoloured in one place cannot disagree with the other —
   D-061's rule that material has one home, applied to a vocabulary rather than
   to a surface. */
/** Editorial pick, by content-layer id. Owner ruling, 2026-08-20. */
const FEATURED: readonly string[] = [
	"us-healthcare-platform",
	"genai-rag-engine",
	"logistics-delivery-platform",
	"gov-regulatory-monitoring"
];

const featured = (): Project[] =>
	FEATURED.map((id: string): Project | null => {
		const project = projects.find((p: Project) => p.id === id) ?? null;
		if (!project && !import.meta.env.PROD) {
			// eslint-disable-next-line no-console
			console.error(`ProjectsTeaser: no project "${id}" in projects.json.`);
		}
		return project;
	}).filter((p: Project | null): p is Project => p !== null);

/**
 * One featured build, and THE WHOLE CARD IS ONE LINK (D-100, owner
 * instruction: every card redirects to the page that holds the detail).
 *
 * Same pattern as the Products and Workshops teasers: a real link on the
 * heading, stretched over the card with `after:inset-0`, which is ONE tab stop
 * and one accessible name. A card wrapped in an anchor AND carrying a heading
 * link would give a keyboard user two stops on one destination.
 *
 * NOTHING IN THE DOM SAYS WHETHER THAT STRETCH ACTUALLY COVERS THE CARD. The
 * overlay is a pseudo-element, so a card that lost `after:inset-0` would still
 * have a working heading link, still have exactly one anchor, and pass every
 * markup check while only the title was clickable — which is why the gate
 * hit-tests `elementFromPoint` at the card's own centre (D-099).
 *
 * WHAT THE CARD SHOWS, AND WHY IT IS MORE THAN IT WAS. It carried the name, the
 * first outcome and the role. It now leads with the category — the same
 * `--cat-*` hue `/projects` gives that category, because here the hue really is
 * information rather than decoration — and adds the employer and the tag rail,
 * so a reader who never clicks still learns what kind of build it was and who
 * it was built for.
 */
const TeaserCard = ({ project, index }: { project: Project; index: number }): JSX.Element => {
	const reduceMotion = useReducedMotion();

	return (
		<motion.li
			className="card-shell group relative"
			initial={reduceMotion ? false : { opacity: 0, y: 24 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.2 }}
			transition={{ duration: 0.5, delay: Math.min(index * 0.06, 0.24), ease: [0.22, 1, 0.36, 1] }}
		>
			<div className="card-face flex h-full flex-col p-6">
				<span
					className={`pill-tint pill-tint-upper pill-tint-wrap self-start ${CATEGORY_ACCENT[project.category]}`}
				>
					{CATEGORY_LABEL[project.category]}
				</span>

				<h3 className="mt-4 text-sm font-bold leading-snug text-[var(--text-strong)]">
					<Link
						to={`/projects#${project.id}`}
						className="rounded-[4px] transition-colors duration-200 after:absolute after:inset-0 after:rounded-[1rem] after:content-[''] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
					>
						{project.name}
					</Link>
				</h3>

				<p className="mt-1 text-sm text-[var(--text-muted)]">
					{project.organization} <span aria-hidden="true">·</span> {project.role}
				</p>

				{/* The first outcome is the card's evidence — a claim with its
				    consequence attached (I.2). Guarded: an entry may carry none. */}
				{project.outcomes.length > 0 && (
					<p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">{project.outcomes[0]}</p>
				)}

				<div className="mt-auto pt-5">
					<div className="flex flex-wrap gap-2">
						{project.tags.map((tag: string) => (
							<span key={tag} className="chip-quiet">
								{tag}
							</span>
						))}
					</div>

					{/* aria-hidden: the heading link above already names the
					    destination, so announcing this would give a screen-reader
					    user two links to one page. */}
					<p
						aria-hidden="true"
						className="mt-4 text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 group-hover:text-[var(--accent-link-hover)]"
					>
						Open the detail &rarr;
					</p>
				</div>
			</div>
		</motion.li>
	);
};

export default function ProjectsTeaser(): JSX.Element {
	const picks = featured();

	return (
		<section className="section-shell" id="projects" aria-labelledby="projects-teaser-heading">
			<div className="section-rail">
				<p className="section-eyebrow">Client &amp; Delivery Work</p>

				<h2 id="projects-teaser-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Projects
					</span>
				</h2>

				<p className="section-standfirst">
					Regulated healthcare, government technology, AI and enterprise systems delivered for clients.
				</p>

				<ul className="mt-10 grid list-none auto-rows-fr grid-cols-1 gap-5 p-0 sm:grid-cols-2 lg:grid-cols-4">
					{picks.map((p: Project, index: number) => (
						<TeaserCard key={p.id} project={p} index={index} />
					))}
				</ul>

				<div className="mt-10">
					<Link
						to="/projects"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						See every client project →
					</Link>
				</div>
			</div>
		</section>
	);
}
